import {
  BookText,
  Brain,
  FileUp,
  Files,
  HelpCircle,
  History,
  LogOut,
  Scale,
  ScrollText,
  Settings as SettingsIcon,
  ShieldHalf,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BrandMark } from '@/components/BrandMark';
import { ListeningButton } from '@/components/ListeningButton';
import { MatterSelector } from '@/components/MatterSelector';
import { MicControls } from '@/components/MicControls';
import { RehearsalToggle } from '@/components/RehearsalToggle';
import { Button } from '@/components/ui';
import { useMatters } from '@/contexts/MatterContext';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';

// Sticky 73px header per 03-ui-design-language.md. backdrop-blur so the
// transcript scroll behind it stays subtly visible without bleeding.
export function Header() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { activeMatterId } = useMatters();
  const docsTo = activeMatterId ? `/admin/matters/${activeMatterId}/documents` : '/matters';
  const isAdmin = user?.role === 'ADMIN';

  async function handleSignOut() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <header
      className="sticky top-0 z-30 border-b border-stone-800/80 bg-stone-950/80 backdrop-blur"
      style={{ height: 73 }}
    >
      <div className="mx-auto flex h-full max-w-screen-2xl items-center justify-between gap-4 px-5 sm:px-6">
        <div className="flex items-center gap-6">
          <Link to="/hearing" className="flex items-center">
            <BrandMark />
          </Link>
          <nav className="hidden items-center gap-4 md:flex">
            <NavLink to="/hearing" label="Hearing" />
            <NavLink to="/matters" label="Matters" icon={<ScrollText className="h-3.5 w-3.5" />} />
            <NavLink to="/sessions" label="Sessions" icon={<History className="h-3.5 w-3.5" />} />
            <NavLink to={docsTo} label="Docs" icon={<Files className="h-3.5 w-3.5" />} />
            <NavLink to="/admin/import" label="Import" icon={<FileUp className="h-3.5 w-3.5" />} />
            {isAdmin ? (
              <NavLink
                to="/admin/legal-skills"
                label="Skills"
                icon={<BookText className="h-3.5 w-3.5" />}
              />
            ) : null}
            {isAdmin ? (
              <NavLink
                to="/admin/authorities"
                label="Authorities"
                icon={<Scale className="h-3.5 w-3.5" />}
              />
            ) : null}
            {isAdmin ? (
              <NavLink
                to="/admin/learning"
                label="Learning"
                icon={<Brain className="h-3.5 w-3.5" />}
              />
            ) : null}
            {isAdmin ? (
              <NavLink to="/admin" label="Admin" icon={<ShieldHalf className="h-3.5 w-3.5" />} />
            ) : null}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <MatterSelector />
          <RehearsalToggle />
          <MicControls />
          <ListeningButton />
          <button
            type="button"
            aria-label="Help"
            onClick={() => window.dispatchEvent(new CustomEvent('cc:open-help'))}
            className="grid h-9 w-9 place-items-center rounded-md text-stone-400 transition-colors hover:bg-stone-800/60 hover:text-stone-200"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          <Link
            to="/settings"
            aria-label="Settings"
            className="grid h-9 w-9 place-items-center rounded-md text-stone-400 transition-colors hover:bg-stone-800/60 hover:text-stone-200"
          >
            <SettingsIcon className="h-4 w-4" />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Sign out"
            onClick={() => void handleSignOut()}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
}

interface NavLinkProps {
  to: string;
  label: string;
  icon?: React.ReactNode;
}

function NavLink({ to, label, icon }: NavLinkProps) {
  const location = useLocation();
  const active = location.pathname.startsWith(to);
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] transition-colors',
        active ? 'text-amber-400' : 'text-stone-500 hover:text-stone-200',
      )}
    >
      {icon}
      {label}
    </Link>
  );
}
